// Profil-Nachbau F-25 Entwurf C (output/profil-team-20261014/cockpit-grafiken-20261019/SCHNITTSTELLEN-C.md 3.2, Auftrag PF-GC-regeln-1):
// die Rechnungen und Sätze des Firmen-Cockpits im Stil „Ziel und Fortschritt“ – Ziel im Zeitraum, Fortschritt, zusammengezählte Kurve mit
// Vorzeitraum und Hochrechnung, Ziellinie, Beispielziel, Zielsatz, Wer mitmacht, Arbeitstage, Gründlichkeit, die Zielblöcke der übrigen
// Seiten und „Was als Nächstes hilft“. Reine Regeln: kein React, kein Netz, keine Uhr (heute kommt herein), kein Zufall, kein Betrag, kein
// fester Produktname; jedes Wort steht in cockpitTexte.ts ZIEL_REGELN. Ziele gelten nur für die ganze Firma, nie je Person.
import { quellenWechsel, rhythmus } from './bericht';
import type { BerichtAbschnitt, Wochentag } from './bericht';
import { aufmerksamkeitFirmaSaetze, vorzeitraumVon } from './berichtTeile';
import type { AufmerksamkeitFirmaLage, TeamLage, TeamZeileKurz } from './berichtTeile';
import { AUFMERKSAMKEIT, BERICHT, EXPORT, PERSONEN, ZIEL_REGELN as ZR } from './cockpitTexte';
import type { Einheit, QuellenArt, QuellenStand } from './cockpitApi';
import { kiCockpitAdresse } from './firmenCockpit';
import { datum, dauer, monatName, tagKurz, tippKopf, zahl, zahlEineStelle } from './format';
import type { Zeitraum } from './zeitraum';
import { ORGANISATION_PFAD, organisationsAdresse } from '../../pages/einstellungen/seiten';

export type Sichtbar = { recherchen: boolean; nachfragen: boolean };
export type ZielTeil = string | { stark: string };
export type ZielText = ZielTeil[];
export type ZielSkalaRegel = { links: string; mitte: string; rechts: string };
/** Angaben für ZielBlock (baustein-1): titel, gross, von, prozent (null = ohne Balken), skala, satz, fuss. */
export type ZielAnzeige = { titel: string; gross: string; von: string; prozent: number | null; skala?: ZielSkalaRegel; satz?: string; fuss?: string };
export type SchrittSymbolRegel = 'person' | 'kalender' | 'quelle' | 'ziel' | 'auge' | 'team' | 'geld' | 'achtung' | 'firma' | 'flagge';
export type SchrittRegel = { key: string; symbol: SchrittSymbolRegel; stark: string; text?: string; link?: { text: string; to: string } };
export type FolgeRegel = { key: string; titel: string; fertig: boolean; text: string | null };
/** Übersicht: zeitraum aus …/uebersicht, recherchen der ganzen Firma bzw. des Teams, vorher = vergleich.recherchen (null ohne Vergleich). */
export type ZielLage = { zeitraum: Zeitraum; heute: string; ziel: number | null; recherchen: number; vorher: number | null; team: boolean; zuWenige: boolean };
export type ZielKurve = { tage: string[]; zeitraum: (number | null)[]; vorzeitraum: (number | null)[] | null; davorTage: string[];
  hochrechnung: (number | null)[] | null; endIndex: number; endwert: number; ziel: { wert: number; text: string } | null };
export type KurvenTipp = { kopf: string; zeilen: { farbe?: 'recherchen' | 'vorzeitraum'; name: string; wert: string }[]; fuss: string };

// ---- Hilfen

const TAG_MS = 86400000;
const alsZahl = (tag: string) => Date.UTC(Number(tag.slice(0, 4)), Number(tag.slice(5, 7)) - 1, Number(tag.slice(8, 10)));
const alsTag = (ms: number) => new Date(ms).toISOString().slice(0, 10);
const plusTag = (tag: string, n: number) => alsTag(alsZahl(tag) + n * TAG_MS);
/** Tage des Monats eines Tags JJJJ-MM-TT (28 bis 31). */
const monatsEnde = (tag: string) => new Date(Date.UTC(Number(tag.slice(0, 4)), Number(tag.slice(5, 7)), 0)).getUTCDate();
/** Der letzte Tag des Monats als JJJJ-MM-TT. */
const letzterTag = (tag: string) => `${tag.slice(0, 8)}${String(monatsEnde(tag)).padStart(2, '0')}`;
/** Zwei Nachkommastellen mit Tausenderpunkt wie format.ts zahl. */
const ZWEI = new Intl.NumberFormat('de-AT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const zwei = (wert: number) => ZWEI.formatToParts(wert).map(teil => teil.type === 'group' ? '.' : teil.value).join('');
const istHeute = (z: Zeitraum) => z.vorgabe === 'heute';
const istJahr = (z: Zeitraum) => z.vorgabe === 'dieses-jahr';
const nurRecherchen = (s: Sichtbar) => s.recherchen && !s.nachfragen;
/** Beide oder keine Reihe sichtbar: „Fragen“; sonst „Recherchen“ bzw. „Nachfragen“. */
function reihenWort(s: Sichtbar): string {
  const W = BERICHT.rhythmus.wort;
  return s.recherchen === s.nachfragen ? W.beide : s.recherchen ? W.recherchen : W.nachfragen;
}
// Die Links der Schritte führen nur auf vorhandene Seiten.
const mitglieder = { text: ZR.schritte.mitglieder, to: organisationsAdresse('members') };
const kontingent = { text: ZR.schritte.kontingent, to: organisationsAdresse('cost') };
const teams = { text: ZR.schritte.teams, to: `${ORGANISATION_PFAD}/teams` };

/** Der Werktag mit den wenigsten Recherchen im Schnitt (bei Gleichstand der erste): nur je Tag, ab 14 Tagen, mit Recherchen an Werktagen
 *  vor heute (rhythmus lässt heute aus; nur heute oder am Wochenende ergibt keinen Schritt). */
function ruhigsterTag(z: Zeitraum, heute: string, einheit: Einheit, abschnitte: BerichtAbschnitt[]): Wochentag | null {
  const werktage = rhythmus(abschnitte, heute).slice(0, 5).filter(t => t.tage > 0);
  if (einheit !== 'TAG' || z.tage < 14 || werktage.reduce((summe, t) => summe + t.recherchen, 0) <= 0) return null;
  return werktage.reduce<Wochentag | null>((x, t) => x === null || t.schnittRecherchen < x.schnittRecherchen ? t : x, null);
}

/** Die Quelle, die deutlich öfter gebraucht wird als davor (bericht.ts quellenWechsel), mit Namen und Anteilen; sonst null. */
function quelleImWechsel(jetzt: QuellenStand, davor: QuellenStand | null, z: Zeitraum):
  { art: QuellenArt; name: string; mehrzahl: boolean; p: number; pv: number } | null {
  for (const teil of quellenWechsel(jetzt, davor, z.vorgabe)) {
    if (typeof teil === 'string') continue;
    const bezug = teil.bezug;
    if (bezug.art !== 'quelle' || bezug.quelle === 'OHNE') continue;
    const art: QuellenArt = bezug.quelle, eintrag = jetzt.arten.find(a => a.art === art);
    if (!eintrag) return null;
    return { art, name: eintrag.name, mehrzahl: BERICHT.quellen.mehrzahl.includes(art), p: eintrag.prozent ?? 0,
      pv: davor?.arten.find(a => a.art === art)?.prozent ?? 0 };
  }
  return null;
}

/** Die Teile ohne Zwischenraum aneinander, fette Teile als Text. */
export function zielTextFlach(t: ZielText): string {
  return t.map(teil => typeof teil === 'string' ? teil : teil.stark).join('');
}

// ---- Rechnen (Entwurf vorschlag-c.html:133-143, :163-166)

/** „Dieser Monat“: alle Tage des Monats (das Ziel gilt für den ganzen Monat); sonst die Tage des Zeitraums. */
export function tageGesamt(z: Zeitraum, heute: string): number {
  return z.vorgabe === 'dieser-monat' ? monatsEnde(z.von) : z.tage;
}
/** Ziel je 30 Tage auf die Tage des Zeitraums gerechnet, mindestens 1. */
export function zielImZeitraum(zielJe30: number, tage: number): number {
  return Math.max(1, Math.round(zielJe30 * tage / 30));
}
/** Ziel je Säule bei Woche (7 Tage) oder Monat. */
export function zielJeAbschnitt(zielJe30: number, einheit: 'WOCHE' | 'MONAT'): number {
  return einheit === 'WOCHE' ? Math.max(1, Math.round(zielJe30 * 7 / 30)) : zielJe30;
}
/** Ganze Prozent des Ziels, nicht gekappt (der Balken kappt); ohne Ziel 0. */
export function fortschritt(ist: number, ziel: number): number {
  return ziel <= 0 ? 0 : Math.round(ist * 100 / ziel);
}
/** Laufende Summe der sichtbaren Reihen (keine sichtbar zählt wie beide); Stellen ohne Abschnitt bleiben null. */
export function zusammengezaehlt(abschnitte: { recherchen: number; nachfragen: number }[], sichtbar: Sichtbar, laenge = abschnitte.length): (number | null)[] {
  const beide = sichtbar.recherchen === sichtbar.nachfragen;
  let summe = 0;
  return Array.from({ length: laenge }, (_, i) => {
    const a = abschnitte[i];
    if (!a) return null;
    summe += (beide || sichtbar.recherchen ? a.recherchen : 0) + (beide || sichtbar.nachfragen ? a.nachfragen : 0);
    return summe;
  });
}
/** Hochrechnung bis Monatsende im laufenden Monat, erst ab dem 3. Tag, nie am letzten Tag und nie ohne Recherchen. */
export function hochrechnung(z: Zeitraum, heute: string, ist: number): { wert: number; ende: string; reihe: (number | null)[] } | null {
  const ende = letzterTag(z.von);
  if (z.vorgabe !== 'dieser-monat' || z.tage < 3 || ist <= 0 || z.bis >= ende) return null;
  const k = tageGesamt(z, heute);
  return { wert: Math.round(ist * k / z.tage), ende,
    reihe: Array.from({ length: k }, (_, i) => i < z.tage - 1 ? null : Math.round(ist * (i + 1) / z.tage)) };
}
/** Beispielziel ohne gesetztes Ziel: auf 30 Tage gerechnet plus 10 %, auf 10 aufgerundet, 10 bis 9.990 (in Zehnteln gerechnet). */
export function beispielZiel(recherchen: number, tage: number): number {
  return Math.min(9990, Math.max(10, Math.ceil(Math.round(recherchen * 330 / tage) / 100) * 10));
}
/** Beispielziel „Antworten mit Quelle“: die nächste Fünferstufe über dem Stand, höchstens 100; ohne Stand 95. */
export function beispielQuellenZiel(mitQuelle: number | null): number {
  if (mitQuelle === null) return 95;
  return mitQuelle >= 100 ? 100 : Math.min(100, Math.ceil((mitQuelle + 1) / 5) * 5);
}

// ---- Zielblock der Übersicht (vorschlag-c.html:221-240)

export function zielStand(l: ZielLage): ZielAnzeige {
  const titel = ZR.titel.uebersicht, fuss = ZR.fuss, z = l.zeitraum, r = l.recherchen;
  if (l.zuWenige) return { titel, gross: '–', von: '', prozent: null, fuss };
  if (l.ziel === null || l.team) return { titel, gross: zahl(r), von: ZR.stand.imZeitraum(r === 1), prozent: null, fuss };
  const zz = zielImZeitraum(l.ziel, tageGesamt(z, l.heute)), p = fortschritt(r, zz);
  const wann = z.vorgabe === 'dieser-monat' ? ZR.stand.imMonat(monatName(z.von)) : istHeute(z) ? ZR.stand.heute : '';
  return { titel, gross: zahl(r), von: ZR.stand.von(zahl(zz), zz === 1) + wann, prozent: p,
    skala: { links: ZR.skala.links, mitte: ZR.skala.erreicht(p), rechts: ZR.skala.ziel(zahl(zz)) }, fuss };
}

export function zielSatz(l: ZielLage): ZielText {
  const S = ZR.satz;
  if (l.zuWenige) return [];
  if (l.ziel === null) return [S.keinZiel];
  if (l.team) return [S.ganzeFirma];
  const z = l.zeitraum, ist = l.recherchen, zz = zielImZeitraum(l.ziel, tageGesamt(z, l.heute));
  const h = hochrechnung(z, l.heute, ist);
  const monatLaeuft = z.vorgabe === 'dieser-monat' && z.bis < letzterTag(z.von);
  const p = (x: number) => fortschritt(x, zz);
  if (h) {
    const tempo = S.tempo(Number(h.ende.slice(8)), monatName(h.ende));
    if (ist < zz) return [tempo, { stark: zahl(h.wert) }, h.wert >= zz ? S.inReichweite : S.fehlen(zahl(zz - h.wert))];
    return [S.erreicht + S.danach + tempo, { stark: zahl(h.wert) }, S.punkt];
  }
  const anfang = ist === zz ? S.erreicht : S.uebertroffen(zahl(ist), zahl(zz));
  if (monatLaeuft) {
    if (ist >= zz) return [anfang, S.punkt];
    return [S.noch, { stark: zahl(zz - ist) }, S.bisZiel + (z.tage < 3 ? S.zuFrueh : '')];
  }
  const v = l.vorher;
  if (ist >= zz) {
    const ende: string = istJahr(z) || v === null ? S.punkt
      : v === 0 ? (istHeute(z) ? S.gesternKeine : S.davorKeine)
      : istHeute(z) ? S.gesternProzent(p(v)) : S.davor(p(v));
    return [anfang + ende];
  }
  const rest = istJahr(z) || v === null || v === 0 ? '' : istHeute(z) ? S.gestern(p(v)) : S.vorzeitraum(p(v));
  return [S.noch, { stark: zahl(zz - ist) }, S.bisZiel + rest];
}

/** Für ZielFestlegen (baustein-1): mit Recht ein Beispiel aus den Daten, ohne Recht wer Ziele setzt. */
export function zielFestlegen(art: 'recherchen' | 'quellen', beispiel: number, darf: boolean): { text: string; beispiel: string } {
  const F = ZR.festlegen;
  if (!darf) return { text: F.keinZiel, beispiel: F.wer };
  return { text: F.text, beispiel: F.beispiel(art === 'recherchen' ? F.recherchen(zahl(beispiel)) : F.quellen(beispiel)) };
}

// ---- Kurve und Ziellinie (vorschlag-c.html:146-209)

/** Ziellinie nur mit Ziel, ohne Team und solange nur Recherchen sichtbar sind (das Ziel zählt Recherchen). */
export function zielLinie(l: { zeitraum: Zeitraum; heute: string; einheit: Einheit; ziel: number | null; sichtbar: Sichtbar; team: boolean }):
  { wert: number; text: string } | null {
  if (l.ziel === null || l.team || !nurRecherchen(l.sichtbar)) return null;
  if (l.einheit === 'WOCHE') {
    const w = zielJeAbschnitt(l.ziel, 'WOCHE');
    return { wert: w, text: ZR.kurve.zielWoche(zahl(w)) };
  }
  if (l.einheit === 'MONAT') return { wert: l.ziel, text: ZR.kurve.zielMonat(zahl(l.ziel)) };
  const w = zielImZeitraum(l.ziel, tageGesamt(l.zeitraum, l.heute));
  return { wert: w, text: ZR.skala.ziel(zahl(w)) };
}

/** Zusammengezählte Kurve je Tag (bei Woche oder Monat zeigt die Seite Säulen mit zielLinie). */
export function zielKurve(l: { zeitraum: Zeitraum; heute: string; jetzt: BerichtAbschnitt[]; davor: BerichtAbschnitt[] | null; sichtbar: Sichtbar;
  ziel: number | null; team: boolean }): ZielKurve {
  const z = l.zeitraum, k = tageGesamt(z, l.heute);
  const tage = Array.from({ length: k }, (_, i) => plusTag(z.von, i));
  const zeitraum = zusammengezaehlt(l.jetzt, l.sichtbar, k);
  const vorzeitraum = l.davor && !istJahr(z) ? zusammengezaehlt(l.davor, l.sichtbar, k) : null;
  const davorVon = vorzeitraumVon(z).von;
  const davorTage = Array.from({ length: z.tage }, (_, i) => plusTag(davorVon, i));
  const endIndex = l.jetzt.length - 1;
  const endwert = endIndex >= 0 ? (zeitraum[Math.min(endIndex, k - 1)] ?? 0) : 0;
  return { tage, zeitraum, vorzeitraum, davorTage, hochrechnung: hochrechnung(z, l.heute, endwert)?.reihe ?? null, endIndex, endwert,
    ziel: zielLinie({ zeitraum: z, heute: l.heute, einheit: 'TAG', ziel: l.ziel, sichtbar: l.sichtbar, team: l.team }) };
}

/** Tooltip eines Tags der Kurve (vorschlag-c.html:176-184). */
export function kurvenTipp(k: ZielKurve, index: number, l: { zeitraum: Zeitraum; heute: string; sichtbar: Sichtbar }): KurvenTipp {
  const tag = k.tage[index], jetzt = k.zeitraum[index], hoch = k.hochrechnung?.[index], davor = k.vorzeitraum?.[index];
  const zeilen: KurvenTipp['zeilen'] = [];
  if (typeof jetzt === 'number') zeilen.push({ farbe: 'recherchen', name: ZR.kurve.bis(tagKurz(tag)), wert: zahl(jetzt) });
  else if (typeof hoch === 'number') zeilen.push({ farbe: 'recherchen', name: ZR.kurve.hochrechnung, wert: ZR.kurve.rund(zahl(hoch)) });
  if (typeof davor === 'number' && index < k.davorTage.length) {
    zeilen.push({ farbe: 'vorzeitraum', name: ZR.kurve.davorBis(tagKurz(k.davorTage[index])), wert: zahl(davor) });
  }
  if (k.ziel) zeilen.push({ name: ZR.kurve.ziel, wert: zahl(k.ziel.wert) });
  return { kopf: tippKopf('TAG', tag, tag, l.heute) + ZR.kurve.tagVon(index + 1, k.tage.length), zeilen,
    fuss: ZR.kurve.fuss(reihenWort(l.sichtbar), tagKurz(l.zeitraum.von)) };
}

export function kurvenTitel(sichtbar: Sichtbar, einheit: Einheit): string {
  const wort = reihenWort(sichtbar);
  return einheit === 'WOCHE' ? ZR.kurve.jeWoche(wort) : einheit === 'MONAT' ? ZR.kurve.jeMonat(wort) : ZR.kurve.zusammen(wort);
}

// ---- Wer mitmacht, Arbeitstage, Gründlichkeit (vorschlag-c.html:248-286)

export function werMitmacht(l: { zeitraum: Zeitraum; personen: number; aktiv: number; aktivDavor: number | null }):
  { satz: string; label: string; legende: { an: string; aus: string }; hinweis: string; an: number; gesamt: number } {
  const M = ZR.mitmachen, n = l.personen, a = Math.min(l.aktiv, n);
  const legende = { an: M.an(zahl(a)), aus: M.aus(zahl(n - a)) };
  if (n <= 0) return { satz: PERSONEN.keine, label: PERSONEN.keine, legende, hinweis: M.hinweis, an: 0, gesamt: 0 };
  const wann = istHeute(l.zeitraum) ? M.heute : M.imZeitraum;
  const kern = n === 1 ? (a === 1 ? M.einzige(wann) : M.einzigeNicht(wann))
    : a === n ? M.alle(zahl(n), wann) : a === 0 ? M.niemand(wann) : M.von(zahl(a), zahl(n), a === 1, wann);
  const ende: string = istJahr(l.zeitraum) || l.aktivDavor === null ? M.punkt : l.aktivDavor === a ? M.wieDavor : M.davor(zahl(l.aktivDavor));
  return { satz: kern + ende, label: M.label(zahl(a), zahl(n), a === 1, n === 1), legende, hinweis: M.hinweis, an: a, gesamt: n };
}

/** Gewonnene Zeit in Arbeitstagen zu 8 Stunden, über 15 Tagen in Arbeitswochen zu 40 Stunden; ohne Regel null (Block entfällt). */
export function arbeitstageText(minuten: number | undefined): { satz: ZielText; label: string } | null {
  if (minuten === undefined) return null;
  const Z = ZR.zeit, d = dauer(minuten), t = minuten / 480;
  if (minuten <= 0) return { satz: [Z.keine], label: Z.labelUnterTag(dauer(0)) };
  if (minuten < 480) return { satz: [{ stark: d }, Z.unterTag], label: Z.labelUnterTag(d) };
  if (t <= 15) return { satz: [{ stark: d }, Z.tage(zahlEineStelle(t))], label: Z.labelTage(d, zahlEineStelle(t)) };
  return { satz: [{ stark: d }, Z.wochen(zahlEineStelle(t / 5))], label: Z.labelWochen(d, zahlEineStelle(t / 5)) };
}

/** „Auf 10 Recherchen kommen 5 Nachfragen“ mit dem Vergleich zum Vorzeitraum. */
export function gruendlichkeitText(l: { zeitraum: Zeitraum; recherchen: number; nachfragen: number; davor: { recherchen: number; nachfragen: number } | null }):
  { text: string; label: string; proZehn: number } {
  const G = ZR.gruendlich, r = l.recherchen, n = l.nachfragen, d = l.davor;
  if (r <= 0) {
    const text = istHeute(l.zeitraum) ? G.keineHeute : G.keine;
    return { text, label: text, proZehn: 0 };
  }
  const proZehn = Math.round(n / r * 10), basis = G.satz(proZehn);
  let ende: string = G.punkt;
  if (!istJahr(l.zeitraum) && d && d.recherchen > 0) {
    const pv = Math.round(d.nachfragen / d.recherchen * 10), jetzt = zwei(n / r), vorher = zwei(d.nachfragen / d.recherchen);
    ende = pv !== proZehn ? G.davor(pv) : jetzt === vorher ? G.gleich : G.wieDavor(jetzt, vorher);
  }
  return { text: basis + ende, label: basis, proZehn };
}

// ---- Was als Nächstes hilft: Übersicht (vorschlag-c.html:288-303)

/** PF-GC-zeitraum-1: ohneFrage sind die Personen ohne Frage im gewählten Zeitraum (wie „Wer mitmacht: ohne Frage“), nicht mehr „seit 30
 *  Tagen“ aus „Aufmerksamkeit“. */
export function schritteUebersicht(l: { zeitraum: Zeitraum; heute: string; search: string; einheit: Einheit; abschnitte: BerichtAbschnitt[];
  ohneFrage: number | null; kontingentFestgelegt?: boolean; quellen: { jetzt: QuellenStand; davor: QuellenStand | null } | null; zuWenige: boolean }): SchrittRegel[] {
  if (l.zuWenige) return [];
  const S = ZR.schritte, liste: SchrittRegel[] = [], ohneFrage = l.ohneFrage ?? 0;
  if (ohneFrage > 0) liste.push({ key: 'inaktiv', symbol: 'person', stark: S.inaktivStark(ohneFrage), text: S.inaktivText(ohneFrage), link: mitglieder });
  if (l.kontingentFestgelegt === false) liste.push({ key: 'kontingent', symbol: 'ziel', stark: S.keinKontingent, text: S.keinKontingentText, link: kontingent });
  const wechsel = l.quellen ? quelleImWechsel(l.quellen.jetzt, l.quellen.davor, l.zeitraum) : null;
  if (wechsel) {
    liste.push({ key: 'quelle', symbol: 'quelle', stark: S.quelleGefragt(wechsel.name, wechsel.mehrzahl, wechsel.p, wechsel.pv), text: S.quelleText,
      link: { text: S.quellen, to: kiCockpitAdresse('quellen', l.search) } });
  }
  const ruhig = ruhigsterTag(l.zeitraum, l.heute, l.einheit, l.abschnitte);
  if (ruhig) {
    liste.push({ key: 'ruhig', symbol: 'kalender', stark: S.ruhig(BERICHT.wochentageAdverbGross[ruhig.tag]),
      text: S.ruhigSchnitt(zahlEineStelle(ruhig.schnittRecherchen)) });
  }
  return liste.slice(0, 4);
}

// ---- Nutzung je Person (seiten-c.js:38-66)

/** Festes Zielbild „alle dabei“: alle Personen der Firma haben gefragt. */
export function zielPerson(l: { zeitraum: Zeitraum; personen: number; aktiv: number; aktivDavor: number | null; namen: boolean }): ZielAnzeige {
  const P = ZR.person, titel = ZR.titel.person, n = l.personen, a = Math.min(l.aktiv, n), f = n - a;
  const fuss = l.namen ? ZR.fuss : P.fuss;
  if (n <= 0) return { titel, gross: zahl(Math.max(0, a)), von: P.von(zahl(n), n === 1), prozent: null, satz: PERSONEN.keine, fuss };
  const p = fortschritt(a, n);
  const davor = istJahr(l.zeitraum) || l.aktivDavor === null ? '' : ' ' + P.davor(zahl(l.aktivDavor), zahl(n));
  return { titel, gross: zahl(a), von: P.von(zahl(n), n === 1), prozent: p,
    skala: { links: ZR.skala.links, mitte: ZR.skala.erreicht(p), rechts: ZR.skala.ziel(zahl(n)) },
    satz: (f > 0 ? P.noch(zahl(f), f === 1) : P.alle) + davor, fuss };
}

/** „Im Schnitt 18,0 Recherchen je aktive Person, an 24 von 30 Tagen.“; tageMit nur je Tag (sonst null). */
export function personSchnittText(l: { recherchen: number; aktiv: number; tageMit: number | null; tage: number }): string | null {
  if (l.aktiv <= 0) return null;
  return ZR.person.schnitt(zahlEineStelle(l.recherchen / l.aktiv))
    + (l.tageMit !== null && l.tage > 1 ? ZR.person.anTagen(zahl(l.tageMit), zahl(l.tage)) : '') + '.';
}

export function schrittePerson(l: { zeitraum: Zeitraum; heute: string; einheit: Einheit; abschnitte: BerichtAbschnitt[]; personen: number;
  aktiv: number; namen: boolean }): SchrittRegel[] {
  const S = ZR.schritte, liste: SchrittRegel[] = [];
  const fehlen = l.personen - Math.min(l.aktiv, l.personen);
  // Am laufenden Tag kann noch jede Person fragen: dann kein Schritt „nichts gefragt“.
  if (fehlen > 0 && !istHeute(l.zeitraum)) liste.push({ key: 'nichts', symbol: 'person', stark: S.nichts(fehlen), text: S.einfuehrung, link: mitglieder });
  const ruhig = ruhigsterTag(l.zeitraum, l.heute, l.einheit, l.abschnitte);
  if (ruhig) liste.push({ key: 'ruhig', symbol: 'kalender', stark: S.ruhig(BERICHT.wochentageAdverbGross[ruhig.tag]) + '.', text: S.termin });
  if (!l.namen) liste.push({ key: 'namen', symbol: 'auge', stark: S.namenAus, text: S.namenAusText });
  return liste.slice(0, 4);
}

// ---- Nutzung je Team (seiten-c.js:68-99)

const mitZahlen = (z: TeamZeileKurz) => !z.zuWenige && z.aktivePersonen !== null;

/** Festes Zielbild „Zahlen je Team“: Gruppen (Teams und „Ohne eigenes Team“) mit Zahlen; ein verdecktes einziges Team zeigt seine Größe. */
export function zielTeam(l: TeamLage): ZielAnzeige {
  const T = ZR.team, titel = ZR.titel.team, fuss = T.fuss, m = l.mindestens;
  if (!l.namen && l.teams.length === 1 && l.teams[0].zuWenige) {
    const team = l.teams[0], k = team.personenHeute ?? 0;
    return { titel, gross: zahl(k), von: T.vonTeam(zahl(m), team.name), prozent: fortschritt(Math.min(k, m), m),
      skala: { links: ZR.skala.links, mitte: T.groesse, rechts: T.noetig(zahl(m)) },
      satz: T.schutzTeam(l.produkt, zahl(m)) + (k < m ? T.darunter(zahl(k), k === 1) : T.zuWenigAktiv), fuss };
  }
  const gruppen = [...l.teams, ...(l.ohneTeam ? [l.ohneTeam] : [])];
  const g = gruppen.length, mit = gruppen.filter(mitZahlen).length;
  if (g === 0) return { titel, gross: zahl(g), von: T.vonGruppen(zahl(g), false), prozent: null, fuss };
  const p = fortschritt(mit, g);
  return { titel, gross: zahl(mit), von: T.vonGruppen(zahl(g), g === 1), prozent: p,
    skala: { links: ZR.skala.links, mitte: ZR.skala.erreicht(p), rechts: ZR.skala.ziel(zahl(g)) },
    satz: l.namen ? T.mitNamen : mit === g ? T.alleGruppen(zahl(m)) : T.schutzGruppe(l.produkt, zahl(m)), fuss };
}

export function schritteTeam(l: TeamLage & { search: string }): SchrittRegel[] {
  const S = ZR.schritte, m = l.mindestens, liste: SchrittRegel[] = [];
  const zeilen = [...l.teams, ...(l.ohneTeam ? [l.ohneTeam] : []), ...(l.geloeschte ? [l.geloeschte] : [])];
  const verdeckt = !l.namen && zeilen.some(z => z.zuWenige);
  if (l.teams.length === 0) liste.push({ key: 'anlegen', symbol: 'team', stark: S.teamsAnlegen, text: S.teamsAnlegenText(m), link: teams });
  else if (verdeckt) liste.push({ key: 'teams', symbol: 'team', stark: S.teamsPflegen, text: S.teamsPflegenText(m), link: teams });
  if (verdeckt) {
    liste.push({ key: 'namen', symbol: 'auge', stark: S.namenEin, text: S.namenEinText,
      link: { text: S.zuPersonen, to: kiCockpitAdresse('personen', l.search) } });
  }
  return liste.slice(0, 4);
}

// ---- Quellen (seiten-c.js:101-123)

/** Ziel „Antworten mit Quelle“ (gespeichert in Prozent); ohne Ziel ohne Balken (die Seite zeigt „Ziel festlegen“). */
export function zielQuellen(l: { zeitraum: Zeitraum; jetzt: QuellenStand; davor: QuellenStand | null; ziel: number | null }): ZielAnzeige {
  const Q = ZR.quellen, titel = ZR.titel.quellen, fuss = ZR.fuss, j = l.jetzt;
  if (j.antworten === 0) return { titel, gross: '–', von: Q.keine, prozent: null, fuss };
  const mit = 100 - (j.ohneTreffer.prozent ?? 0), ohne = j.ohneTreffer.anzahl;
  const davor = !istJahr(l.zeitraum) && l.davor && l.davor.antworten > 0 ? l.davor : null;
  const satz = (davor ? Q.davor(100 - (davor.ohneTreffer.prozent ?? 0)) + ' ' : '')
    + (ohne === 0 ? BERICHT.quellen.alleMitQuelle : Q.ohne(zahl(ohne), ohne === 1));
  const basis = { titel, gross: Q.gross(mit), von: Q.von(zahl(j.antworten), j.antworten === 1), satz, fuss };
  if (l.ziel === null) return { ...basis, prozent: null };
  return { ...basis, prozent: mit, skala: { links: ZR.skala.links, mitte: mit >= l.ziel ? Q.erreicht : Q.noch(l.ziel - mit), rechts: Q.ziel(l.ziel) } };
}

export function schritteQuellen(l: { zeitraum: Zeitraum; jetzt: QuellenStand; davor: QuellenStand | null; ziel: number | null }): SchrittRegel[] {
  const S = ZR.schritte, j = l.jetzt, liste: SchrittRegel[] = [];
  if (j.antworten === 0) return [];
  const wechsel = quelleImWechsel(j, l.davor, l.zeitraum);
  if (wechsel) liste.push({ key: 'quelle', symbol: 'quelle', stark: S.quelleKurz(wechsel.name, wechsel.mehrzahl), text: S.quelleZahlen(wechsel.p, wechsel.pv) });
  const land = j.arten.find(a => a.art === 'LANDESRECHT');
  if (land && (land.prozent ?? 0) < 10) {
    // „kam nicht vor“ nur ohne jede Antwort daraus; ein auf 0 % gerundeter Anteil „kommt selten vor“.
    const p = land.prozent ?? 0;
    liste.push({ key: 'land', symbol: 'flagge', stark: land.antworten === 0 ? S.landNie(land.name) : S.landSelten(land.name, p), text: S.landText });
  }
  if (l.ziel !== null) {
    const po = j.ohneTreffer.prozent ?? 0, mit = 100 - po;
    liste.push({ key: 'ziel', symbol: 'ziel', stark: S.ohneQuelle(po), text: mit >= l.ziel ? S.zielErreicht(l.ziel) : S.zielFehlt(l.ziel, l.ziel - mit) });
  }
  return liste.slice(0, 4);
}

// ---- Aufmerksamkeit (seiten-c.js:126-138)

/** Festes Zielbild „alles in Ordnung“: Prüfungen ohne Hinweis (Zahl der Prüfungen nach den Rechten, berichtTeile.ts). */
export function zielAufmerksamkeit(l: AufmerksamkeitFirmaLage): ZielAnzeige {
  const A = ZR.aufmerksamkeit, { hinweise, pruefungen } = aufmerksamkeitFirmaSaetze(l), ok = pruefungen - hinweise;
  const satz = hinweise === 0 ? A.alleOk
    : hinweise === 1 ? (l.inaktiv > 0 ? A.nurInaktiv(l.inaktiv) : (l.grenze ?? 0) > 0 ? A.nurGrenze : A.nurKontingent)
    : A.mehrere(hinweise);
  return { titel: ZR.titel.aufmerksamkeit, gross: zahl(ok), von: A.von(pruefungen), prozent: fortschritt(ok, pruefungen),
    skala: { links: ZR.skala.links, mitte: hinweise === 0 ? A.inOrdnung : A.offen(hinweise), rechts: ZR.skala.ziel(zahl(pruefungen)) }, satz };
}

/** Kontingent-Schritte nur mit costs.read: grenze sonst null, kontingent und kontingentFestgelegt sonst undefined. */
export function schritteAufmerksamkeit(l: AufmerksamkeitFirmaLage & { kontingentFestgelegt?: boolean }): SchrittRegel[] {
  const S = ZR.schritte, liste: SchrittRegel[] = [], grenze = l.grenze ?? 0;
  if (l.inaktiv > 0) {
    liste.push({ key: 'inaktiv', symbol: 'person', stark: S.einfuehrungAlle, text: l.namen ? S.inaktivMitNamen : S.inaktivOhneNamen(l.inaktiv), link: mitglieder });
  }
  if (grenze > 0) liste.push({ key: 'grenze', symbol: 'achtung', stark: S.grenzen, text: S.grenzenText(grenze), link: kontingent });
  if (l.kontingent) {
    liste.push({ key: 'kontingent', symbol: 'achtung', stark: AUFMERKSAMKEIT.kontingent + ':', text: S.kontingentText(l.kontingent.prozent, l.kontingent.monat, l.kontingent.inRecherchen),
      link: kontingent });
  }
  if (l.kontingentFestgelegt === false) liste.push({ key: 'festlegen', symbol: 'ziel', stark: S.festlegen, text: S.festlegenText, link: kontingent });
  return liste.slice(0, 4);
}

// ---- Export (seiten-c.js:140-167)

/** Festes Zielbild „In drei Schritten zur Datei“: der Zeitraum ist immer gewählt. */
export function exportFortschritt(stand: { gewaehlt: boolean; fertig: boolean }): ZielAnzeige {
  const E = ZR.export, n = 1 + (stand.gewaehlt ? 1 : 0) + (stand.fertig ? 1 : 0);
  return { titel: ZR.titel.export, gross: zahl(n), von: E.von, prozent: Math.round(n * 100 / 3),
    skala: { links: ZR.skala.links, mitte: stand.fertig ? E.bereit : stand.gewaehlt ? E.laden : E.waehlen, rechts: E.fertig } };
}

export function exportFolge(z: { von: string; bis: string; tage: number }, stand: { gewaehlt: boolean; fertig: boolean }, plattform: boolean): FolgeRegel[] {
  const E = ZR.export;
  return [
    { key: 'zeitraum', titel: E.zeitraum, fertig: true, text: E.spanne(datum(z.von), datum(z.bis), z.tage) },
    { key: 'datei', titel: E.datei, fertig: stand.gewaehlt, text: null },
    { key: 'laden', titel: plattform ? E.ladenPlattform : E.ladenFirma, fertig: stand.fertig,
      text: stand.fertig ? EXPORT.fertig : plattform ? E.hinweisPlattform : E.hinweisFirma },
  ];
}
